<template>
  <div class="page-container">
    <div ref="techTalentPage" class="tech-talent-page" :class="{ 'locale-zh-CN': isZhLocale }">
      <!-- 页面头部 -->
      <PageHeader :config="config" @module-click="handleModuleClick" />

      <!-- 面包屑导航 -->
      <Breadcrumb :items="breadcrumbItems" />

      <!-- 头部Banner -->
      <SearchBanner 
        :title-main1="techConfig.techTalent.title1"
        :title-main2="techConfig.techTalent.title2"
        :subtitle="techConfig.techTalent.subtitle"
        :placeholder="techConfig.techTalent.searchPlaceholder"
        :button-text="techConfig.techTalent.searchButton"
        :keyword.sync="searchKeyword"
        :background-image="techConfig.techTalent?.assets?.bannerBg || localeImg('kejirencai/head-bg@2x.png')"
        search-object="talent"
        @search="handleSearch"
      />

      <!-- 主体内容区 -->
      <div class="main-content" ref="talentSection">
        <!-- 筛选条件区 -->
        <div class="filter-section">
          <!-- 筛选条件行 -->
          <div class="filter-row">
            <!-- 领域筛选 -->
            <div class="filter-item">
              <span class="filter-label">{{ techConfig.techTalent.filters.domain }}</span>
              <div class="filter-tags">
                <div 
                  v-for="item in domainOptions.length > 0 ? domainOptions : (techConfig.techTalent.domainList || [])" 
                  :key="item.value || item"
                  class="filter-tag"
                  :class="{ active: selectedDomain === (item.value || item) }"
                  @click="handleFilterChange('domain', item.value || item)"
                >
                  {{ item.label || item }}
                </div>
              </div>
            </div>
          </div>

          <div class="filter-row">
            <!-- 人才类型筛选 -->
            <div class="filter-item">
              <span class="filter-label">{{ techConfig.techTalent.filters.talentType }}</span>
              <div class="filter-tags">
                <div 
                  v-for="item in talentTypeOptions.length > 0 ? talentTypeOptions : (techConfig.techTalent.talentTypeList || [])" 
                  :key="item.value || item"
                  class="filter-tag"
                  :class="{ active: selectedTalentType === (item.value || item) }"
                  @click="handleFilterChange('talentType', item.value || item)"
                >
                  {{ item.label || item }}
                </div>
              </div>
            </div>
          </div>

          <div class="filter-row">
            <!-- 省份地区筛选 -->
            <div class="filter-item">
              <span class="filter-label">{{ techConfig.techTalent.filters.province }}</span>
              <div class="filter-tags">
                <div 
                  v-for="item in provinceOptions.length > 0 ? provinceOptions : (techConfig.techTalent.provinceList || [])" 
                  :key="item.value || item"
                  class="filter-tag"
                  :class="{ active: selectedProvince === (item.value || item) }"
                  @click="handleFilterChange('province', item.value || item)"
                >
                  {{ item.label || item }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 人才列表区 -->
        <div v-if="loading" class="loading">{{ getText('common.loading') }}</div>
        <TalentList 
          v-else
          :talent-list="talentList"
          type="talent"
          :columns="4"
          @talent-click="handleTalentClick"
        />

        <!-- 分页 -->
        <div class="pagination">
          <span class="page-info">
            {{ techConfig.techTalent.pagination.pageInfo
                .replace('{total}', totalCount)
                .replace('{pageSize}', pageSize) }}
          </span>
          <div class="page-controls">
            <button
              class="page-btn"
              :disabled="currentPage === 1"
              @click="changePage(1)"
            >{{ techConfig.techTalent.pagination.firstPage || '' }}</button>
            <button 
              class="page-btn" 
              :disabled="currentPage === 1"
              @click="changePage(currentPage - 1)"
            >{{ techConfig.techTalent.pagination.prevPage }}</button>
            <button 
              v-for="page in visiblePages" 
              :key="page"
              :class="['page-num', { active: page === currentPage }]"
              @click="changePage(page)"
            >{{ page }}</button>
            <button 
              class="page-btn" 
              :disabled="currentPage === totalPages"
              @click="changePage(currentPage + 1)"
            >{{ techConfig.techTalent.pagination.nextPage }}</button>
            <button
              class="page-btn"
              :disabled="currentPage === totalPages"
              @click="changePage(totalPages)"
            >{{ techConfig.techTalent.pagination.lastPage || '' }}</button>
          </div>
        </div>
      </div>

      <!-- 页脚 -->
      <PageFooter />
    </div>
  </div>
</template>

<script>
import { applyPageScale, setupPageScaleObserver, teardownPageScaleObserver } from '@/utils/pageScale'
import { localeImg } from '@/utils/localeAsset'
import { getHomeConfig } from '@/config/homeConfig'
import { getTechTalentConfig } from '@/config/techTalentConfig'
import PageHeader from '@/components/PageHeader.vue'
import PageFooter from '@/components/PageFooter.vue'
import Breadcrumb from '@/components/Breadcrumb.vue'
import SearchBanner from '@/components/SearchBanner.vue'
import TalentList from '@/components/TalentList.vue'
import supplyDemandApi from '@/api/supplyDemand'
import techTalentApi from '@/api/techTalent'
import { getCurrentLocale, getText as i18nGetText } from '@/utils/i18nText'

export default {
  name: 'TechTalent',
  components: {
    PageFooter,
    PageHeader,
    Breadcrumb,
    SearchBanner,
    TalentList
  },
  data() {
    return {
      config: {},
      techConfig: {},
      searchKeyword: '',
      
      // 面包屑
      breadcrumbItems: [],
      
      // 筛选条件
      selectedDomain: 'all',
      selectedTalentType: 'all',
      selectedProvince: 'all',
      
      // 筛选选项（从字典获取）
      domainOptions: [],
      talentTypeOptions: [],
      provinceOptions: [],
      
      // 人才列表数据
      talentList: [],
      loading: false,
      
      // 分页相关
      currentPage: 1,
      pageSize: 12,
      totalCount: 0
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalCount / this.pageSize)
    },
    
    visiblePages() {
      const pages = []
      const total = this.totalPages
      const current = this.currentPage
      let start = Math.max(1, current - 2)
      let end = Math.min(total, current + 2)
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      return pages
    }
  },
  created() {
    this.loadConfig()
  },
  watch: {
    selectedDomain() {
      this.currentPage = 1
      this.loadTalentList()
    },
    selectedTalentType() {
      this.currentPage = 1
      this.loadTalentList()
    },
    selectedProvince() {
      this.currentPage = 1
      this.loadTalentList()
    },
    currentPage() {
      this.loadTalentList()
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.setScale()
      this.observePageResize()
    })
    window.addEventListener('resize', this.setScale)
  },
  beforeDestroy() {
    teardownPageScaleObserver(this)
    window.removeEventListener('resize', this.setScale)
  },
  methods: {
    setScale() {
      applyPageScale(this.getPageScaleEl())
    },

    getPageScaleEl() {
      return this.$refs.techTalentPage
    },

    observePageResize() {
      setupPageScaleObserver(this, () => this.getPageScaleEl())
    },
    getText(key) {
      return i18nGetText(key)
    },
    
    async loadConfig() {
      // 加载首页配置（用于头部和页脚）
      this.config = getHomeConfig(getCurrentLocale())
      // 加载科技人才配置
      this.techConfig = getTechTalentConfig(getCurrentLocale())
      
      // 初始化面包屑
      const breadcrumb = this.techConfig.techTalent?.breadcrumb || {}
      this.breadcrumbItems = [
        { label: breadcrumb.home || '', path: '/index' },
        { label: breadcrumb.current || '', path: '' }
      ]
      
      // 加载字典数据
      await this.loadDictData()
      // 加载人才列表
      this.loadTalentList()
    },

    // 加载字典数据（领域、人才类型、省市区）
    async loadDictData() {
      try {
        // 人才类型字典 key：experttype
        const dictCodeList = ['yField', 'experttype', 'fjkjtarea']
        const response = await supplyDemandApi.getDictData(dictCodeList)
        const result = response.data || {}
        
        if (result.status === 0) {
          // 处理响应数据结构
          let dictData = []
          if (result.data && Array.isArray(result.data.rows)) {
            dictData = result.data.rows
          } else if (Array.isArray(result.data)) {
            dictData = result.data
          } else if (typeof result.data === 'object' && result.data !== null) {
            dictData = Object.values(result.data).flat()
          }
          
          // 确保 dictData 是数组
          if (!Array.isArray(dictData)) {
            dictData = []
          }
          
          console.log('[TechTalent] 字典原始数据:', dictData)
          
          // 处理领域（yField）
          const fieldDict = dictData
            .filter(item => item && item.dictCode === 'yField')
            .sort((a, b) => (a.sortNum || 0) - (b.sortNum || 0))
          
          this.domainOptions = [
            { label: '全部', value: 'all' },
            ...fieldDict.map(item => ({
              label: item.itemValue || '',
              value: item.itemCode || ''
            }))
          ]
          console.log('[TechTalent] 领域字典 yField:', fieldDict)
          console.log('[TechTalent] 领域筛选 options:', this.domainOptions)
          
          // 处理人才类型（experttype）
          const expertTypeDict = dictData
            .filter(item => item && item.dictCode === 'experttype')
            .sort((a, b) => (a.sortNum || 0) - (b.sortNum || 0))
          
          this.talentTypeOptions = [
            { label: '全部', value: 'all' },
            ...expertTypeDict.map(item => ({
              label: item.itemValue || '',
              value: item.itemCode || ''
            }))
          ]
          console.log('[TechTalent] 人才类型字典 experttype:', expertTypeDict)
          console.log('[TechTalent] 人才类型筛选 options:', this.talentTypeOptions)
          
          // 处理省市区（fjkjtarea）- 只取省份级别（parentCode 为 0）
          const provinceDict = dictData
            .filter(item => {
              if (!item || item.dictCode !== 'fjkjtarea') return false
              // 只要一级：parentCode 为 0 或 '0'
              return item.parentCode === 0 || item.parentCode === '0'
            })
            .sort((a, b) => (a.sortNum || 0) - (b.sortNum || 0))
          
          this.provinceOptions = [
            { label: '全部', value: 'all' },
            ...provinceDict.map(item => ({
              label: item.itemValue || '',
              value: item.itemCode || ''
            }))
          ]
          console.log('[TechTalent] 省份字典 fjkjtarea(省级):', provinceDict)
          console.log('[TechTalent] 省份筛选 options:', this.provinceOptions)
          
          // 初始化默认选择
          this.selectedDomain = this.domainOptions[0]?.value || ''
          this.selectedTalentType = this.talentTypeOptions[0]?.value || ''
          this.selectedProvince = this.provinceOptions[0]?.value || ''
        } else {
          // 如果接口失败，使用配置中的默认选项
          this.domainOptions = this.techConfig.techTalent?.domainList?.map(item => ({ label: item, value: item })) || []
          this.talentTypeOptions = this.techConfig.techTalent?.talentTypeList?.map(item => ({ label: item, value: item })) || []
          this.provinceOptions = this.techConfig.techTalent?.provinceList?.map(item => ({ label: item, value: item })) || []
          console.warn('[TechTalent] 字典接口非 0，使用配置回退 options:', {
            domainOptions: this.domainOptions,
            talentTypeOptions: this.talentTypeOptions,
            provinceOptions: this.provinceOptions
          })
        }
      } catch (error) {
        console.error('加载字典数据失败:', error)
        // 使用配置中的默认选项
        this.domainOptions = this.techConfig.techTalent?.domainList?.map(item => ({ label: item, value: item })) || []
        this.talentTypeOptions = this.techConfig.techTalent?.talentTypeList?.map(item => ({ label: item, value: item })) || []
        this.provinceOptions = this.techConfig.techTalent?.provinceList?.map(item => ({ label: item, value: item })) || []
        console.warn('[TechTalent] 字典请求异常，使用配置回退 options:', {
          domainOptions: this.domainOptions,
          talentTypeOptions: this.talentTypeOptions,
          provinceOptions: this.provinceOptions
        })
      }
    },

    // 加载人才列表
    async loadTalentList() {
      this.loading = true
      try {
        const params = {
          pageNum: this.currentPage,
          pageSize: this.pageSize,
          mailingCommunications: this.selectedProvince === 'all' ? '' : this.selectedProvince,
          field: this.selectedDomain === 'all' ? '' : this.selectedDomain,
          type: this.selectedTalentType === 'all' ? '' : this.selectedTalentType,
          // 搜索：按“专家名称”模糊查询
          name: String(this.searchKeyword || '').trim()
        }
        console.log('[TechTalent] 列表请求参数:', params)
        
        const response = await techTalentApi.getExpertPageList(params)
        const result = response.data || {}
        if (result.status === 0 && result.data) {
          const list = result.data.list || []
          const avatar = this.techConfig.techTalent?.assets?.avatarImg
          
          this.talentList = list.map(item => {
            // 处理领域显示（industrialSystem可能是逗号分隔的多个领域代码）
            let researchText = '所属领域：'
            if (item.industrialSystem) {
              const fieldCodes = item.industrialSystem.split(',')
              const fieldLabels = fieldCodes.map(code => {
                const field = this.domainOptions.find(opt => opt.value === code)
                return field ? field.label : code
              }).filter(Boolean)
              researchText += fieldLabels.join('、') || ''
            } else {
              researchText += '未设置'
            }
            
            // 处理地区显示（需要根据省市区代码查找对应的标签）
            let regionText = '所属地区：'
            if (item.mailingCommunications || item.mailingCity || item.countyOrganId) {
              const province = this.provinceOptions.find(opt => opt.value === item.mailingCommunications)
              regionText += (province ? province.label : '') || ''
            } else {
              regionText += '未设置'
            }
            
            // 处理人才类型显示
            let titleText = ''
            if (item.expertTypeFirst) {
              const talentType = this.talentTypeOptions.find(opt => opt.value === item.expertTypeFirst)
              titleText = talentType ? talentType.label : '专家'
            } else {
              titleText = '专家'
            }
            
            return {
              id: item.id,
              name: item.name || '',
              title: titleText,
              research: researchText,
              region: regionText,
              avatar: item.photoPath || avatar
            }
          })
          
          // 更新总数
          this.totalCount = result.data.total || 0
        } else {
          this.talentList = []
          this.totalCount = 0
        }
      } catch (error) {
        console.error('加载人才列表失败:', error)
        this.talentList = []
        this.totalCount = 0
      } finally {
        this.loading = false
      }
    },
    
    handleModuleClick(module) {
      if (module.id === 'home') {
        this.$router.push('/index')
      } else if (module.id === 'innovation') {
        this.$router.push('/innovation')
      } else if (module.id === 'supply') {
        this.$router.push('/supply-demand')
      }
    },
    
    handleSearch() {
      console.log('搜索:', this.searchKeyword)
      this.currentPage = 1
      this.loadTalentList()
    },

    // 筛选条件变化
    handleFilterChange(type, value) {
      if (type === 'domain') {
        this.selectedDomain = value
      } else if (type === 'talentType') {
        this.selectedTalentType = value
      } else if (type === 'province') {
        this.selectedProvince = value
      }
      // watch 会自动触发 loadTalentList
    },
    
    handleTalentClick(talent) {
      this.$router.push({
        path: '/tech-talent/detail',
        query: { id: talent.id }
      })
    },
    
    changePage(page) {
      if (page < 1 || page > this.totalPages) return
      this.currentPage = page
      // watch 会自动触发 loadTalentList
      const talentSection = this.$refs.talentSection
      if (talentSection) {
        talentSection.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.page-container {
  width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
}

.tech-talent-page {
  width: 1920px;
  min-height: 100vh;
  background: #fff;
  transform-origin: top left;
}

/* 主体内容区 */
.main-content {
  width: 1700px;
  margin: 0 auto;
  padding: 20px 0;
  
  .loading {
    text-align: center;
    padding: 40px;
    font-size: 14px;
    color: #666666;
  }
}

/* 筛选条件区 */
.filter-section {
  background: #FFFFFF;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 20px;
  margin-bottom: 20px;
}

.filter-row {
  margin-bottom: 15px;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.filter-item {
  display: flex;
  align-items: flex-start;
}

.filter-label {
  font-size: 14px;
  color: #333333;
  margin-right: 15px;
  width: 80px;
  padding-top: 8px;
  flex-shrink: 0;
}

.filter-tags {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.filter-tag {
  padding: 6px 15px;
  background: #FFFFFF;
  border: 1px solid #DDDDDD;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 13px;
  color: #666666;
  line-height: 1.4;
  
  &:hover {
    background: #FFF8E6;
    border-color: #A0903C;
    color: #A0903C;
  }
  
  &.active {
    background: #A0903C;
    border-color: #A0903C;
    color: #FFFFFF;
  }
}

/* 分页 */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #FFFFFF;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 20px 30px;
  margin-bottom: 40px;
}

.page-info {
  font-size: 13px;
  color: #666666;
}

.page-controls {
  display: flex;
  align-items: center;
  gap: 6px;
}

.page-btn,
.page-num {
  min-width: 32px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid #DDDDDD;
  background: #FFFFFF;
  border-radius: 4px;
  font-size: 13px;
  color: #666666;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover:not(:disabled):not(.active) {
    border-color: #A0903C;
    color: #A0903C;
    background: #FFF8E6;
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
    background: #F5F5F5;
  }
  
  &.active {
    background: #A0903C;
    border-color: #A0903C;
    color: #FFFFFF;
    font-weight: 500;
  }
}




@import '@/assets/scss/mixins/portal-filter-locale-zh-cn.scss';

.tech-talent-page.locale-zh-CN {
  @include portal-filter-locale-zh-cn;
}
</style>